/**
 * use-payment-provider-config.ts — 框架通道（PAYERMAX / AIRWALLEX）配置与回调登记 API 封装（ADM-PAY-S012 / A8 §10）
 *
 * 对应后端 `/api/v1/integrations/payment-providers/:channel/*`：能力声明与配置列表 viewer+，
 * 其余写端点 root/admin + mfa。全部写端点必带 UUID v7 `Idempotency-Key`（由调用方按「用户意图」生成并在
 * 重试时复用，见 `~/lib/idempotency-key`）。
 *
 * **秘密边界**：`credential` / `material` 只在请求体出现一次；响应不含凭据、密文与验签材料，
 * 前端不缓存、不回填。请求 / 响应类型一律取自 `@novel-admin/shared`。
 */

import type {
  PaymentProviderCapabilityResponse,
  PaymentProviderChannel,
  PaymentProviderConfigResponse,
  PaymentProviderConnectionTestResponse,
  PaymentProviderVerificationKeyWriteResponse,
  PaymentProviderWebhookEndpointRegistrationResponse,
  PaymentProviderWebhookEndpointStateResponse,
  RegisterPaymentProviderWebhookEndpointRequest,
  UpsertPaymentProviderConfigRequest,
} from '@novel-admin/shared';
import { apiRequest } from '~/api/client';

const base = (channel: PaymentProviderChannel) =>
  `/api/v1/integrations/payment-providers/${channel}`;

const idem = (key: string) => ({ 'Idempotency-Key': key });

export type PaymentProviderEndpointAction = 'activate' | 'deactivate' | 'retire';
export type PaymentProviderConfigAction = 'test' | 'activate' | 'deactivate';

export const paymentProviderConfigApi = {
  /** 能力声明子集 + 当前部署环境 mode（§10.0） */
  capability: (channel: PaymentProviderChannel) =>
    apiRequest<PaymentProviderCapabilityResponse>({
      method: 'GET',
      path: `${base(channel)}/capability`,
    }),

  /** 各 App 配置（有界不分页；不含凭据） */
  listConfigs: (channel: PaymentProviderChannel) =>
    apiRequest<PaymentProviderConfigResponse[]>({
      method: 'GET',
      path: `${base(channel)}/configs`,
    }),

  /** 新建或整份替换配置；不激活，任何变更回到 unverified（§10.1） */
  upsertConfig: (
    channel: PaymentProviderChannel,
    appId: string,
    body: UpsertPaymentProviderConfigRequest,
    idempotencyKey: string,
  ) =>
    apiRequest<PaymentProviderConfigResponse>({
      method: 'PUT',
      path: `${base(channel)}/configs/${appId}`,
      body,
      headers: idem(idempotencyKey),
    }),

  /** 只读连接探针（§10.1.1）；无 body */
  testConnection: (channel: PaymentProviderChannel, appId: string, idempotencyKey: string) =>
    apiRequest<PaymentProviderConnectionTestResponse>({
      method: 'POST',
      path: `${base(channel)}/configs/${appId}/test`,
      headers: idem(idempotencyKey),
    }),

  /** 启用 / 停用（无 body）；启用要求已 connected，不跑探针 */
  changeConfigState: (
    channel: PaymentProviderChannel,
    appId: string,
    action: 'activate' | 'deactivate',
    idempotencyKey: string,
  ) =>
    apiRequest<PaymentProviderConfigResponse>({
      method: 'POST',
      path: `${base(channel)}/configs/${appId}/${action}`,
      headers: idem(idempotencyKey),
    }),

  /** 按商户账户组登记回调 endpoint（§10.2） */
  registerEndpoint: (
    channel: PaymentProviderChannel,
    body: RegisterPaymentProviderWebhookEndpointRequest,
    idempotencyKey: string,
  ) =>
    apiRequest<PaymentProviderWebhookEndpointRegistrationResponse>({
      method: 'POST',
      path: `${base(channel)}/webhook-endpoints`,
      body,
      headers: idem(idempotencyKey),
    }),

  /** endpoint 启停 / 退役（无 body；退役不可恢复） */
  changeEndpointState: (
    channel: PaymentProviderChannel,
    endpointId: string,
    action: PaymentProviderEndpointAction,
    idempotencyKey: string,
  ) =>
    apiRequest<PaymentProviderWebhookEndpointStateResponse>({
      method: 'POST',
      path: `${base(channel)}/webhook-endpoints/${endpointId}/${action}`,
      headers: idem(idempotencyKey),
    }),

  /** 写入新验签材料并整组轮转（§10.2）；`material` 只写 */
  writeVerificationKey: (
    channel: PaymentProviderChannel,
    endpointId: string,
    material: string,
    idempotencyKey: string,
  ) =>
    apiRequest<PaymentProviderVerificationKeyWriteResponse>({
      method: 'PUT',
      path: `${base(channel)}/webhook-endpoints/${endpointId}/verification-keys`,
      body: { material },
      headers: idem(idempotencyKey),
    }),
};
